import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import CommunityErrorFallback from '../../../../shared/ErrorHandling/CommunityErrorFallback/index'
import { usePostHogEvent } from '../../../../../PostHog/index'
import { useTranslatedText } from '../../../../../StaticText/StaticTextContext'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../../Loading/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../../Button/index'
import ChevronDown from '../../../../../Icon/icons/ChevronDown/index'
import ChevronUp from '../../../../../Icon/icons/ChevronUp/index'
import type { OraclePlaylistResult } from '../../../../../../services/community/oracle/useOracleSearch'
import { enrichPlaylistItems, computeTotalDuration } from '../../../../../../services/community/identity/library/content-utils'
import type { UseDiscoverTabStateReturn } from '../../../../../../services/community/identity/library/useDiscoverTabState'

import type {
  PlaylistItem,
  EnrichedPlaylistItem,
} from '../../../../../../services/community/types'
import DiscoverPlaylistRow from '../../../shared/PlaylistRow/DiscoverPlaylistRow'
import DiscoverStreamingText from './DiscoverStreamingText'

const DISCOVER_INITIAL_COUNT = 8

export interface ProfileDiscoverTabProps {
  /**
   * Lifted Discover-tab state. Owned by `PlaylistTabContainer` so it
   * survives tab switches — this component is pure presentation.
   */
  discover: UseDiscoverTabStateReturn
  followedIds: Set<string>
  followerCounts: Record<string, number>
  unfollowToast: { playlistId: string; playlistName: string } | null
  onFollow: (id: string) => Promise<void>
  onUnfollow: (id: string, name: string) => void
  onUndoUnfollow: (id: string) => void
  onDismissUnfollow: (id: string) => Promise<void>
  seeMoreLabel: string
  showLessLabel: string
}

function ProfileDiscoverTab ({
  discover,
  followedIds,
  followerCounts,
  unfollowToast,
  onFollow,
  onUnfollow,
  onUndoUnfollow,
  onDismissUnfollow,
  seeMoreLabel,
  showLessLabel,
}: ProfileDiscoverTabProps) {
  // i18n via the nearest <StaticTextProvider> (set up by PlaylistTabContainer's
  // Outer with the `profileCollectionsTabs` slice).
  const discoverSearchingText = useTranslatedText({ keys: ['discoverSearching'] })
  const tryAgainText = useTranslatedText({ keys: ['tryAgain'] })
  const showLessText = useTranslatedText({ keys: ['showLess'] })
  const seeMoreText = useTranslatedText({ keys: ['seeMore'] })
  const discoverEmptyText = useTranslatedText({ keys: ['discoverEmpty'] })
  // Row-level labels resolved at the parent and threaded down so the
  // variant's React.memo can short-circuit (no internal StaticTextContext
  // subscription).
  const byAuthorTemplate = useTranslatedText({ keys: ['byAuthor'] })
  const byAnonymousLabel = useTranslatedText({ keys: ['byAnonymous'] })
  const noLongerFollowingPrefix = useTranslatedText({ keys: ['noLongerFollowing'] })
  const undoLabel = useTranslatedText({ keys: ['undo'] })

  const {
    activeResults,
    activeError,
    activeLoading,
    discoverStreamState,
    streamingText,
    oracleRefine,
    retry,
    discoverExpanded,
    setDiscoverExpanded,
  } = discover

  // ── Discover tab view + refinement tracking ─────────────────────────────
  // discover_viewed fires once per mount (this component only renders when
  // the Discover tab is active).  discover_refined wraps `oracleRefine` so
  // every refinement click emits before the network request leaves.
  //
  // Per-row track* callbacks (followed / video clicked / item played) are
  // also registered here — once per tab mount instead of once per row —
  // and threaded down to `DiscoverPlaylistRow` as props. Matches the
  // `ProfileFollowingTab` → `FollowedPlaylistRow` pattern.
  const trackDiscoverViewed = usePostHogEvent('community_discover_viewed')
  const trackDiscoverRefined = usePostHogEvent('community_discover_refined')
  const trackDiscoverPlaylistFollowed = usePostHogEvent('community_discover_playlist_followed')
  const trackDiscoverVideoClicked = usePostHogEvent('community_discover_video_clicked')
  const trackPlaylistItemPlayed = usePostHogEvent('community_playlist_item_played')
  const hasTrackedDiscoverView = useRef(false)
  useEffect(() => {
    if (hasTrackedDiscoverView.current) return
    hasTrackedDiscoverView.current = true
    trackDiscoverViewed()
  }, [trackDiscoverViewed])

  const handleRefineClick = useCallback((refinementText: string) => {
    trackDiscoverRefined(refinementText)
    oracleRefine(refinementText)
  }, [oracleRefine, trackDiscoverRefined])

  const handleToggleDiscoverExpanded = useCallback(() => {
    setDiscoverExpanded((prev) => !prev)
  }, [setDiscoverExpanded])

  const hasDiscoverResults = activeResults.playlists.length > 0
  const visibleDiscoverPlaylists = discoverExpanded
    ? activeResults.playlists
    : activeResults.playlists.slice(0, DISCOVER_INITIAL_COUNT)

  const enrichedDiscoverData = useMemo(() => {
    const map: Record<string, { items: EnrichedPlaylistItem[]; totalDuration: string | null }> = {}
    activeResults.playlists.forEach((result) => {
      if (!result.playlist) return
      const playlistItems = (result.playlist.playlistItems || []) as PlaylistItem[]
      map[result.playlist.id] = {
        // Discover surfaces user-authored playlists from other creators —
        // every episode the author added must render as its own video tile.
        // No pivot, no dedupe.
        items: enrichPlaylistItems(playlistItems, {
          dedupe: false,
          pivotEpisodesToSeries: false,
        }),
        totalDuration: computeTotalDuration(playlistItems),
      }
    })
    return map
  }, [activeResults])

  const renderDiscoverRow = useCallback((result: OraclePlaylistResult, position: number) => {
    const { playlist } = result
    if (!playlist) return null
    const enriched = enrichedDiscoverData[playlist.id]
    const authorName = playlist.userProfile?.displayName || null
    const authorDisplay = authorName
      ? byAuthorTemplate.replace('{name}', authorName)
      : byAnonymousLabel
    return (
      // Per-row error isolation — a crash in one discover row no longer
      // takes down the whole Discover tab.
      <ErrorBoundary
        key={playlist.id}
        FallbackComponent={CommunityErrorFallback}
        resetKeys={[playlist.id]}
      >
        <DiscoverPlaylistRow
          playlist={playlist}
          enrichedItems={enriched ? enriched.items : []}
          totalDuration={enriched ? enriched.totalDuration : null}
          followedIds={followedIds}
          followerCounts={followerCounts}
          unfollowToast={unfollowToast}
          authorDisplay={authorDisplay}
          onFollow={onFollow}
          onUnfollow={onUnfollow}
          onUndoUnfollow={onUndoUnfollow}
          onDismissUnfollow={onDismissUnfollow}
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
          noLongerFollowingPrefix={noLongerFollowingPrefix}
          undoLabel={undoLabel}
          position={position}
          trackDiscoverPlaylistFollowed={trackDiscoverPlaylistFollowed}
          trackDiscoverVideoClicked={trackDiscoverVideoClicked}
          trackPlaylistItemPlayed={trackPlaylistItemPlayed}
        />
      </ErrorBoundary>
    )
  }, [
    followedIds, followerCounts, unfollowToast, enrichedDiscoverData,
    byAuthorTemplate, byAnonymousLabel,
    noLongerFollowingPrefix, undoLabel,
    onFollow, onUnfollow,
    onUndoUnfollow, onDismissUnfollow,
    seeMoreLabel, showLessLabel,
    trackDiscoverPlaylistFollowed,
    trackDiscoverVideoClicked,
    trackPlaylistItemPlayed,
  ])

  return (
    <div className="playlist-tab-container__discover">
      {activeLoading && (
        <div className="playlist-tab-container__discover-loading">
          <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
          <p className="playlist-tab-container__discover-loading-text">
            {discoverSearchingText}
          </p>
        </div>
      )}
      {discoverStreamState === 'STREAMING' && (
        <DiscoverStreamingText
          text={streamingText}
          onRefineClick={handleRefineClick}
          isStreaming
        />
      )}
      {activeError && !activeLoading && discoverStreamState !== 'STREAMING' && (
        <div className="playlist-tab-container__empty">
          <p className="playlist-tab-container__empty-text">{activeError}</p>
          <Button
            type={BUTTON_TYPES.GHOST}
            onClick={retry}
          >
            {tryAgainText}
          </Button>
        </div>
      )}
      {discoverStreamState === 'RESULTS' && (
        <div className="playlist-tab-container__discover-results">
          {streamingText && (
            <DiscoverStreamingText
              text={streamingText}
              onRefineClick={handleRefineClick}
              isStreaming={false}
            />
          )}
          {hasDiscoverResults && (
            <div className="playlist-tab-container__discover-list">
              {visibleDiscoverPlaylists.map(renderDiscoverRow)}
            </div>
          )}
          {/*
            NB: Discover preloads the full Oracle response client-side and
            toggles visibility with DISCOVER_INITIAL_COUNT (used here and in
            the idle-state branch below). Intentional — Oracle returns the
            complete set in one response, so there's no `loadMore` to wire
            up. The "See More / Show Less" pattern is a UI affordance, not
            pagination.
          */}
          {activeResults.playlists.length > DISCOVER_INITIAL_COUNT ? (
            <Button
              type={BUTTON_TYPES.LINK}
              className="playlist-tile-grid__toggle"
              onClick={handleToggleDiscoverExpanded}
            >
              {discoverExpanded ? showLessText : seeMoreText}
              {discoverExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          ) : null}
        </div>
      )}
      {discoverStreamState === 'IDLE' && !activeLoading && !activeError && !hasDiscoverResults && (
        <div className="playlist-tab-container__empty">
          <p className="playlist-tab-container__empty-text">
            {discoverEmptyText}
          </p>
        </div>
      )}
      {discoverStreamState === 'IDLE' && !activeLoading && !activeError && hasDiscoverResults && (
        <div className="playlist-tab-container__discover-results">
          <div className="playlist-tab-container__discover-list">
            {visibleDiscoverPlaylists.map(renderDiscoverRow)}
          </div>
          {activeResults.playlists.length > DISCOVER_INITIAL_COUNT ? (
            <Button
              type={BUTTON_TYPES.LINK}
              className="playlist-tile-grid__toggle"
              onClick={handleToggleDiscoverExpanded}
            >
              {discoverExpanded ? showLessText : seeMoreText}
              {discoverExpanded ? <ChevronUp /> : <ChevronDown />}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

// React.memo closes the memoization gap so all three tab components
// (MyPlaylists, Following, Discover) short-circuit on shallow compare.
// Props from PlaylistTabContainer are now reference-stable via the
// useMemo wrappers in useCollectionsData + usePlaylistFollowMutations.
export default React.memo(ProfileDiscoverTab)
